import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { MAX_MORTGAGE_DURATION_IN_MONTHS } from '../../../constants/mortgage';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'shared-input-term',
  imports: [InputNumberModule, FormsModule, IconComponent],
  templateUrl: './input-term.component.html',
  styleUrls: ['./input-term.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTermComponent),
      multi: true,
    },
  ],
})
export class InputTermComponent implements ControlValueAccessor {
  @Input() shortPlaceholder = false;
  @Input() maxYears = MAX_MORTGAGE_DURATION_IN_MONTHS / 12;

  private readonly cdRef = inject(ChangeDetectorRef);

  private _months: number | undefined;
  private _years: number | undefined;
  private _isDisabled = false;

  set months(months: number) {
    this._months = months;
    this.changeCallback(this.calculateDuration(months, this.years));
  }

  get months(): number | undefined {
    return this._months;
  }

  set years(years: number) {
    this._years = years;
    if (years >= MAX_MORTGAGE_DURATION_IN_MONTHS / 12) {
      this._months = 0;
    }
    this.changeCallback(this.calculateDuration(this.months, years));
  }

  get years(): number | undefined {
    return this._years;
  }

  get disabled(): boolean {
    return this._isDisabled;
  }

  onBlur(): void {
    this.touchCallback();
  }

  writeValue(value: number): void {
    this._months = value % 12;
    this._years = Math.floor(value / 12);
    this.cdRef.markForCheck();
  }

  changeCallback: (value: number) => void = () => null;
  touchCallback: () => void = () => null;

  registerOnChange(fn: never): void {
    this.changeCallback = fn;
  }

  registerOnTouched(fn: never): void {
    this.touchCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    this.cdRef.detectChanges();
  }

  private calculateDuration(months?: number, years?: number): number {
    return Number(years) * 12 + Number(months); // TODO: Could possibly equal NaN
  }
}
